import React from "react";
import { useParams, useLocation, Navigate, Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { POLICIES, POLICY_META } from "../data/policies";
import SEO, { breadcrumbSchema } from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";

export default function Policy({ slug: slugProp }) {
  const params = useParams();
  const location = useLocation();
  const slug = slugProp || params.slug;
  const policy = POLICIES.find((p) => p.slug === slug);
  if (!policy) return <Navigate to="/" replace />;

  const plainTitle = policy.title.replace(/&amp;/g, "&");
  // Honour custom top-level routes (e.g. /environmental-plan) by using the actual pathname
  const path = location.pathname || `/policies/${slug}`;
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Policies", path: "/policies" },
    { name: policy.shortTitle.replace(/&amp;/g, "&"), path },
  ];

  const otherPolicies = POLICIES.filter((p) => p.slug !== slug);

  return (
    <div data-testid={`page-policy-${slug}`}>
      <SEO
        title={`${plainTitle} · Oxford Environmental Consultants`}
        description={policy.description}
        path={path}
        schema={breadcrumbSchema(crumbs)}
      />

      <section className="bg-[#0A0F1C] text-white pt-16 pb-20 lg:pt-24 lg:pb-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-8"><Breadcrumb items={crumbs} theme="dark" /></div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-5">Policies · {policy.category}</p>
          <h1 className="font-display text-4xl lg:text-6xl font-bold tracking-tight leading-[1.08] max-w-4xl" dangerouslySetInnerHTML={{ __html: policy.title }} />
          <p className="mt-6 text-lg text-white/70 max-w-3xl leading-relaxed">{policy.description}</p>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 space-y-8">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">Document control</p>
                <dl className="text-sm space-y-3">
                  <div className="flex justify-between gap-4 border-b border-slate-200 pb-2">
                    <dt className="text-slate-500">Owner</dt>
                    <dd className="text-slate-900 font-medium">{POLICY_META.company}</dd>
                  </div>
                  <div className="flex justify-between gap-4 border-b border-slate-200 pb-2">
                    <dt className="text-slate-500">Category</dt>
                    <dd className="text-slate-900 font-medium">{policy.category}</dd>
                  </div>
                  <div className="flex justify-between gap-4 border-b border-slate-200 pb-2">
                    <dt className="text-slate-500">Last reviewed</dt>
                    <dd className="text-slate-900 font-medium">{POLICY_META.lastReviewed}</dd>
                  </div>
                  <div className="flex justify-between gap-4 border-b border-slate-200 pb-2">
                    <dt className="text-slate-500">Next review</dt>
                    <dd className="text-slate-900 font-medium">{POLICY_META.nextReview}</dd>
                  </div>
                </dl>
              </div>
              <div className="p-6 bg-[#0A0F1C] text-white">
                <p className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-3">Need to raise this?</p>
                <p className="text-sm text-white/80 leading-relaxed mb-4">Contact our Quality / Data Protection Lead.</p>
                <p className="text-sm"><a href={`mailto:${POLICY_META.contactEmail}`} className="text-white underline">{POLICY_META.contactEmail}</a></p>
                <p className="text-sm mt-1">{POLICY_META.phone}</p>
              </div>
            </div>
          </aside>

          <article className="lg:col-span-8">
            <div
              className="prose prose-slate max-w-none
                prose-headings:font-display prose-headings:tracking-tight prose-headings:text-slate-900
                prose-h3:text-2xl prose-h3:font-semibold prose-h3:mt-12 prose-h3:mb-4 first:prose-h3:mt-0
                prose-p:text-slate-700 prose-p:leading-[1.75] prose-p:text-base
                prose-strong:text-slate-900 prose-strong:font-semibold
                prose-ul:text-slate-700 prose-li:my-1
                prose-a:text-[#1FA59A] prose-a:no-underline hover:prose-a:underline
                [&_section]:mb-2"
              dangerouslySetInnerHTML={{ __html: policy.body }}
            />
            <div className="mt-14 pt-8 border-t border-slate-200 text-sm text-slate-500">
              <p><strong className="text-slate-700">Version:</strong> Reviewed {POLICY_META.lastReviewed}. Next review {POLICY_META.nextReview}.</p>
              <p className="mt-2">This policy is maintained by {POLICY_META.company} and forms part of our Integrated Management System certified by {POLICY_META.certificationBody}.</p>
            </div>
          </article>
        </div>
      </section>

      <section className="bg-[#F8FAFC] border-t border-slate-200 py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-6">Other policies</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {otherPolicies.map((p) => {
              const customPaths = {
                "environmental-plan": "/environmental-plan",
                "terms-of-service": "/termsofservice",
              };
              const to = customPaths[p.slug] || `/policies/${p.slug}`;
              return (
              <Link key={p.slug} to={to} className="group bg-white border border-slate-200 hover:border-slate-900 p-5 transition-colors" data-testid={`policy-link-${p.slug}`}>
                <div className="flex items-start justify-between mb-3">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{p.category}</p>
                  <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-slate-900 transition-colors" />
                </div>
                <p className="font-display text-base font-semibold text-slate-900" dangerouslySetInnerHTML={{ __html: p.shortTitle }} />
              </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
